import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '@state/hooks'
import { createConversation, setActiveConversation, deleteConversationAsync, renameConversationAsync } from '@state/slices/conversationsSlice'
import { useIsSampleCompany, SAMPLE_COMPANY_TOOLTIP } from '@state/hooks/useIsSampleCompany'
import './ConversationList.css'

function ConversationList() {
  const dispatch = useAppDispatch()
  const { conversations, activeConversationId } = useAppSelector(state => state.conversations)
  const { activeCompanyId } = useAppSelector(state => state.companies)
  const isSampleCompany = useIsSampleCompany()

  // Rename state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState<string>('')
  const [editError, setEditError] = useState<string | null>(null)

  // Filter conversations for active company
  const companyConversations = conversations.filter(c => c.companyId === activeCompanyId)

  const handleNewConversation = () => {
    if (!activeCompanyId) {
      alert('Please select a company first')
      return
    }
    dispatch(createConversation({ title: 'New Conversation', companyId: activeCompanyId }))
  }

  const handleSelectConversation = (id: string) => {
    dispatch(setActiveConversation(id))
  }

  const handleDeleteConversation = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('Delete this conversation? This action cannot be undone.')) {
      try {
        await dispatch(deleteConversationAsync(id)).unwrap()
      } catch (error) {
        alert(`Failed to delete conversation: ${error}`)
      }
    }
  }

  const handleStartRename = (id: string, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (isSampleCompany) return
    setEditingId(id)
    setEditingTitle(currentTitle)
    setEditError(null)
  }

  const handleSaveRename = async (id: string) => {
    const trimmedTitle = editingTitle.trim()

    if (!trimmedTitle) {
      setEditError('Title cannot be empty')
      return
    }

    if (trimmedTitle.length > 200) {
      setEditError('Title must be 200 characters or less')
      return
    }

    try {
      await dispatch(renameConversationAsync({ conversationId: id, newTitle: trimmedTitle })).unwrap()
      setEditingId(null)
      setEditingTitle('')
      setEditError(null)
    } catch (error: any) {
      setEditError(error || 'Failed to rename conversation')
    }
  }

  const handleCancelRename = () => {
    setEditingId(null)
    setEditingTitle('')
    setEditError(null)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveRename(id)
    } else if (e.key === 'Escape') {
      handleCancelRename()
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return 'Today'
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  const getConversationIcon = (conversation: any) => {
    // Determine icon based on conversation content
    if (conversation.strategyWorkspaceData?.meceTable) {
      return '📊' // Has MECE table
    } else if (conversation.strategyWorkspaceData?.strategyMap) {
      return '🎯' // Has strategy map
    } else if (conversation.messages && conversation.messages.length > 5) {
      return '💬' // Extended conversation
    } else {
      return '📝' // Basic conversation
    }
  }

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <h2>Conversations</h2>
        <button
          className="new-conversation-btn"
          onClick={handleNewConversation}
          title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'New Conversation'}
          disabled={isSampleCompany}
        >
          + New
        </button>
      </div>

      <div className="conversation-items">
        {!activeCompanyId ? (
          <div className="empty-state">
            <p>No company selected</p>
            <p className="hint">Select a company above to view conversations</p>
          </div>
        ) : companyConversations.length === 0 ? (
          <div className="empty-state">
            <p>No conversations yet</p>
            <p className="hint">Click "+ New" to start</p>
          </div>
        ) : (
          companyConversations.map((conv) => {
            const isEditing = editingId === conv.id

            return (
              <div
                key={conv.id}
                className={`conversation-item ${conv.id === activeConversationId ? 'active' : ''}`}
                onClick={() => !isEditing && handleSelectConversation(conv.id)}
              >
                <div className="conversation-icon">
                  {getConversationIcon(conv)}
                </div>
                <div className="conversation-info">
                  {isEditing ? (
                    <div className="conversation-title-edit">
                      <input
                        type="text"
                        className="conversation-title-input"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={(e) => handleRenameKeyDown(e, conv.id)}
                        onBlur={() => handleSaveRename(conv.id)}
                        autoFocus
                        maxLength={200}
                      />
                      {editError && <div className="edit-error">{editError}</div>}
                    </div>
                  ) : (
                    <>
                      <div
                        className="conversation-title"
                        onDoubleClick={(e) => handleStartRename(conv.id, conv.title, e)}
                        title={conv.title}
                      >
                        {conv.title}
                      </div>
                      <div className="conversation-meta">
                        <span className="conversation-date">{formatDate(conv.updatedAt)}</span>
                        <span className="conversation-count">{(conv.messageCount ?? conv.messages?.length ?? 0)} msgs</span>
                      </div>
                    </>
                  )}
                </div>
                {!isEditing && (
                  <>
                    <button
                      className="rename-conversation-btn"
                      onClick={(e) => handleStartRename(conv.id, conv.title, e)}
                      title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Rename conversation (or double-click the title)'}
                      disabled={isSampleCompany}
                    >
                      ✏️
                    </button>
                    <button
                      className="delete-conversation-btn"
                      onClick={(e) => handleDeleteConversation(conv.id, e)}
                      title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Delete conversation'}
                      disabled={isSampleCompany}
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default ConversationList
